import { AgentComponentMode, AgentComponentType } from '@reach/shared-types/agent-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useId } from 'react';
import { appRoutes } from '../../../app-routes';
import type { LaunchDarklyFlags } from '../../../types/launch-darkly';
import { MODE_COPY, NAV_COPY, OVERVIEW_COPY, TEMPLATE_PART_COPY } from '../agents-copy';
import { ModeBadge } from '../ModeBadge';
import { useAgentComponentCount } from '../useAgentComponents';
import styles from './AgentsOverview.module.css';
import { TemplatePartRow } from './TemplatePartRow';

const MODES = Object.values(AgentComponentMode);

/** Explains how components, templates and tenants fit together, with the partner's counts. */
export const AgentsOverview = () => {
  const stepsHeadingId = useId();
  const partsHeadingId = useId();
  const componentsHeadingId = useId();
  const toolsHeadingId = useId();
  const modesHeadingId = useId();
  const flags = useFlags<LaunchDarklyFlags>();
  const { data: skillCount } = useAgentComponentCount(AgentComponentType.SKILL);
  const { data: guidanceCount } = useAgentComponentCount(AgentComponentType.GUIDANCE);
  const { data: escalationCount } = useAgentComponentCount(AgentComponentType.ESCALATION);

  const skillCountIsLoaded = skillCount !== undefined;
  const guidanceCountIsLoaded = guidanceCount !== undefined;
  const escalationCountIsLoaded = escalationCount !== undefined;
  const skillsLinkLabel = skillCountIsLoaded
    ? OVERVIEW_COPY.skillCount(skillCount)
    : OVERVIEW_COPY.viewSkills;
  const guidanceLinkLabel = guidanceCountIsLoaded
    ? OVERVIEW_COPY.guidanceCount(guidanceCount)
    : OVERVIEW_COPY.viewGuidance;
  const escalationsLinkLabel = escalationCountIsLoaded
    ? OVERVIEW_COPY.escalationCount(escalationCount)
    : OVERVIEW_COPY.viewEscalations;
  const toolsPageIsAvailable = flags['enable-partner-mcp-authoring'] === true;
  const toolsLink = toolsPageIsAvailable ? appRoutes.AGENT_TOOLS : undefined;

  return (
    <article className={styles.overview}>
      <h1 className={styles.title}>{NAV_COPY.overview}</h1>
      <p className={styles.intro}>{OVERVIEW_COPY.intro}</p>

      <section aria-labelledby={stepsHeadingId}>
        <h2 id={stepsHeadingId}>{OVERVIEW_COPY.stepsHeading}</h2>
        <ol className={styles.steps}>
          {OVERVIEW_COPY.steps.map(step => (
            <li key={step.title}>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby={partsHeadingId}>
        <h2 id={partsHeadingId}>{OVERVIEW_COPY.partsHeading}</h2>
        <section className={styles.partGroup} aria-labelledby={componentsHeadingId}>
          <h3 id={componentsHeadingId}>{OVERVIEW_COPY.componentsHeading}</h3>
          <p className={styles.sectionDescription}>{OVERVIEW_COPY.componentsDescription}</p>
          <ul className={styles.parts}>
            <TemplatePartRow
              part={TEMPLATE_PART_COPY.guidance}
              linkLabel={guidanceLinkLabel}
              to={appRoutes.AGENT_GUIDANCE}
            />
            <TemplatePartRow
              part={TEMPLATE_PART_COPY.skills}
              linkLabel={skillsLinkLabel}
              to={appRoutes.AGENT_SKILLS}
            />
            <TemplatePartRow
              part={TEMPLATE_PART_COPY.escalations}
              linkLabel={escalationsLinkLabel}
              to={appRoutes.AGENT_ESCALATIONS}
            />
          </ul>
        </section>
        <section className={styles.partGroup} aria-labelledby={toolsHeadingId}>
          <h3 id={toolsHeadingId}>{OVERVIEW_COPY.toolsHeading}</h3>
          <p className={styles.sectionDescription}>{OVERVIEW_COPY.toolsDescription}</p>
          <ul className={styles.parts}>
            <TemplatePartRow
              part={TEMPLATE_PART_COPY.tools}
              linkLabel={OVERVIEW_COPY.toolsLink}
              to={toolsLink}
            />
          </ul>
        </section>
      </section>

      <section aria-labelledby={modesHeadingId}>
        <h2 id={modesHeadingId}>{OVERVIEW_COPY.modesHeading}</h2>
        <p className={styles.sectionDescription}>{OVERVIEW_COPY.modesDescription}</p>
        <ul className={styles.modes}>
          {MODES.map(mode => (
            <li key={mode}>
              <ModeBadge mode={mode} />
              <p>{MODE_COPY[mode].description}</p>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
};
